import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 20,
    fontSize: 10,
    fontFamily: "Helvetica",
    position: "relative",
  },
  // Full header only on the first page
  headerContainer: {
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: "column",
    alignItems: "center",
  },
  reportTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 4,
    textAlign: "center",
  },
  schoolInfo: {
    fontSize: 12,
    marginBottom: 4,
    textAlign: "center",
  },
  schoolBold: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 4,
    textAlign: "center",
  },
  headerRight: {
    fontSize: 10,
    textAlign: "center",
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#999",
    marginBottom: 10,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderColor: "#999",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ccc",
  },
  tableHeaderCell: {
    textAlign: "center",
    padding: 3,
    fontSize: 9,
    borderRightWidth: 1,
    borderColor: "#999",
  },
  tableCell: {
    textAlign: "center",
    padding: 3,
    fontSize: 9,
    borderRightWidth: 1,
    borderColor: "#ccc",
  },
  signatureContainer: {
    marginTop: 20,
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  signatureText: {
    fontSize: 10,
    marginRight: 40,
  },
  pageFooter: {
    position: "absolute",
    bottom: 10,
    left: 20,
    right: 20,
    fontSize: 9,
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 6,
    marginTop: 6,
    textAlign: "center",
  },
  note: {
    fontSize: 9,
    marginTop: 6,
    textAlign: "center",
  },
});

// Helper: Indian format; show "-" for 0
const formatValue = (val) => (Number(val) === 0 ? "-" : Number(val).toLocaleString("en-IN"));

// Admission number (various shapes)
const getAdmissionNo = (s) =>
  s?.AdmissionNumber ??
  s?.admission_number ??
  s?.admissionNo ??
  s?.admission_no ??
  s?.id ??
  "";

// Student id (fallback)
const getStudentId = (s) =>
  Number(s?.id ?? s?.student_id ?? s?.Student_ID ?? s?.StudentId ?? s?.Student?.id ?? 0);

// Unique fee headings across all students
const getUniqueFeeHeadings = (studentData) => {
  const headings = new Set();
  studentData.forEach((student) => {
    (student.feeDetails || []).forEach((fee) => headings.add(fee.fee_heading));
  });
  return Array.from(headings);
};

// Map heading -> finalAmountDue for a student
const mapStudentFeeDetails = (student) => {
  const feeMap = {};
  (student.feeDetails || []).forEach((fee) => {
    feeMap[fee.fee_heading] = fee.finalAmountDue;
  });
  return feeMap;
};

// Header (repeats every page)
const TableHeader = ({ feeHeadings }) => (
  <View style={styles.tableHeaderRow}>
    <Text style={[styles.tableHeaderCell, { flex: 0.8 }]}>Sr. No</Text>
    <Text style={[styles.tableHeaderCell, { flex: 1.2 }]}>Admission No.</Text>
    <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Student Name</Text>
    {/* NEW: Previous Balance column */}
    <Text style={[styles.tableHeaderCell, { flex: 1.2 }]}>Previous Balance</Text>
    {feeHeadings.map((heading, idx) => (
      <Text key={idx} style={[styles.tableHeaderCell, { flex: 1.2 }]}>
        {heading}
      </Text>
    ))}
  </View>
);

const PdfStudentDueReport = ({
  school,
  selectedClass,
  studentData,
  headSummary,
  grandSummary,
  // NEW: pass this from StudentDueTable (optional)
  prevBalanceMap = {},
  // Optional: let caller pass a display label for session (e.g., "2025-26")
  sessionLabel = "2025-26",
}) => {
  const feeHeadings = getUniqueFeeHeadings(studentData);
  const today = new Date().toLocaleDateString("en-IN");
  const className = selectedClass;

  // Pagination
  const recordsPerPage = 20;
  const totalPages = Math.ceil(studentData.length / recordsPerPage);
  const studentPages = [];

  for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
    const pageData = studentData.slice(
      pageIndex * recordsPerPage,
      (pageIndex + 1) * recordsPerPage
    );

    studentPages.push(
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        key={`student-page-${pageIndex}`}
      >
        {/* Full header only on first page */}
        {pageIndex === 0 && (
          <View style={styles.headerContainer}>
            <View style={styles.headerLeft}>
              <Text style={styles.reportTitle}>{school?.name}</Text>
              <Text style={styles.schoolBold}>{school?.description}</Text>
              <Text style={styles.schoolBold}>Student Fee Due Report</Text>
              <Text style={styles.schoolInfo}>Session: {sessionLabel}</Text>
              <Text style={styles.schoolInfo}>Class: {className}</Text>
            </View>
            <View style={styles.headerRight}>
              <Text>As on: {today}</Text>
            </View>
          </View>
        )}

        {/* Table */}
        <View style={styles.table}>
          <TableHeader feeHeadings={feeHeadings} />
          {pageData.map((student, index) => {
            const feeMap = mapStudentFeeDetails(student);
            const sid = getStudentId(student);
            const prevBal = Number(prevBalanceMap[sid] || 0);
            return (
              <View style={styles.tableRow} key={`${sid}-${index}`}>
                <Text style={[styles.tableCell, { flex: 0.8 }]}>
                  {pageIndex * recordsPerPage + index + 1}
                </Text>
                <Text style={[styles.tableCell, { flex: 1.2 }]}>{getAdmissionNo(student)}</Text>
                <Text style={[styles.tableCell, { flex: 2 }]}>{student.name}</Text>
                {/* NEW: Previous Balance cell */}
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 1.2 },
                  ]}
                >
                  {formatValue(prevBal)}
                </Text>
                {feeHeadings.map((heading, idx) => (
                  <Text key={idx} style={[styles.tableCell, { flex: 1.2 }]}>
                    {feeMap[heading] ? formatValue(feeMap[heading]) : "-"}
                  </Text>
                ))}
              </View>
            );
          })}
        </View>

        {/* Signature */}
        <View style={styles.signatureContainer}>
          <Text style={styles.signatureText}>Signature: ___________________</Text>
        </View>

        <Text
          style={styles.pageFooter}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    );
  }

  // Summary page
  const summaryPage = (
    <Page size="A4" orientation="landscape" style={styles.page} key="summary-page">
      <View style={styles.headerContainer}>
        <View style={styles.headerLeft}>
          <Text style={styles.sectionTitle}>Headwise Summary</Text>
        </View>
        <View style={styles.headerRight}>
          <Text>As on: {today}</Text>
        </View>
      </View>

      <View style={styles.table}>
        <View style={styles.tableHeaderRow}>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Fee Heading</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Original Due</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Effective Due</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Final Due</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Received</Text>
        </View>

        {Object.entries(headSummary).map(([heading, summary]) => (
          <View style={styles.tableRow} key={heading}>
            <Text style={[styles.tableCell, { flex: 2 }]}>{heading}</Text>
            <Text style={[styles.tableCell, { flex: 2 }]}>{formatValue(summary.originalFeeDue)}</Text>
            <Text style={[styles.tableCell, { flex: 2 }]}>{formatValue(summary.effectiveFeeDue)}</Text>
            <Text style={[styles.tableCell, { flex: 2 }]}>{formatValue(summary.finalAmountDue)}</Text>
            <Text style={[styles.tableCell, { flex: 2 }]}>{formatValue(summary.totalFeeReceived)}</Text>
          </View>
        ))}

        <View style={styles.tableRow}>
          <Text style={[styles.tableCell, { flex: 2, fontWeight: "bold" }]}>Grand Total</Text>
          <Text style={[styles.tableCell, { flex: 2, fontWeight: "bold" }]}>
            {formatValue(grandSummary.originalFeeDue)}
          </Text>
          <Text style={[styles.tableCell, { flex: 2, fontWeight: "bold" }]}>
            {formatValue(grandSummary.effectiveFeeDue)}
          </Text>
          <Text style={[styles.tableCell, { flex: 2, fontWeight: "bold" }]}>
            {formatValue(grandSummary.finalAmountDue)}
          </Text>
          <Text style={[styles.tableCell, { flex: 2, fontWeight: "bold" }]}>
            {formatValue(grandSummary.totalFeeReceived)}
          </Text>
        </View>
      </View>

      <Text style={styles.note}>
        Note: “Previous Balance” is shown per student on the detail pages and is not included in headwise totals.
      </Text>

      <View style={styles.signatureContainer}>
        <Text style={styles.signatureText}>Signature: ___________________</Text>
      </View>

      <Text
        style={styles.pageFooter}
        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
        fixed
      />
    </Page>
  );

  return (
    <Document>
      {studentPages}
      {summaryPage}
    </Document>
  );
};

export default PdfStudentDueReport;
